import { AppField, FieldsResponse } from "@/types/WorkflowCreate/fields";
import { GroupByAndAggregateTransformationConfig } from "../types/groupByAndAggregate";
import { TransformationStepType } from "@/constants/workflows/workflowCreate";
import { ADD_CUSTOM_LABEL_FIELD_RESPONSE, CUSTOM_LABEL_CODE } from "@/constants/common";
import { MapObjectsConfiguration, ObjectConfigPayload as MapObjectConfigPayload } from "../types/mapObjects";
import { FanoutTransformationConfig } from "../types/fanout";
import { FlatteningFieldsConfiguration, ObjectConfigPayload as FlattenObjectConfigPayload } from "../types/flattenFields";
import { AddNewFieldsConfiguration } from "../types/addNewFields";

const cloneAppFields = (fields: AppField[] = []): AppField[] =>
  fields.map((field) => ({
    ...field,
    fields: cloneAppFields(field.fields || [])
  }));

const mergeFirstLayerStringFields = (
  currentFields: AppField[],
  additionalPreviousFields?: FieldsResponse
): AppField[] => {
  if (!additionalPreviousFields?.responseFields?.length) {
    return currentFields;
  }

  const mergedFields = [...currentFields];
  const existingStringCodes = new Set(
    currentFields.filter((field) => field.type === "STRING").map((field) => field.code)
  );

  additionalPreviousFields.responseFields.forEach((field) => {
    if (field.type !== "STRING" || existingStringCodes.has(field.code)) {
      return;
    }

    mergedFields.push({
      ...field,
      fields: [],
      order: mergedFields.length
    });
    existingStringCodes.add(field.code);
  });

  return mergedFields;
};

export const buildTransformationSchema = (
  // Should be union of different transformation configs
  config: GroupByAndAggregateTransformationConfig | MapObjectsConfiguration | FanoutTransformationConfig | FlatteningFieldsConfiguration | AddNewFieldsConfiguration,
  transformationType: TransformationStepType,
  additionalPreviousFields?: FieldsResponse
): FieldsResponse => {
  const defaultTransformationFields: FieldsResponse = {
    requestFields: [...ADD_CUSTOM_LABEL_FIELD_RESPONSE.requestFields],
    responseFields: [...ADD_CUSTOM_LABEL_FIELD_RESPONSE.responseFields]
  };

  switch (transformationType) {
    /**
     * GROUP_AND_AGGREGATE
     */
    case TransformationStepType.GROUP_AND_AGGREGATE: {
      const inputConfiguration = config as GroupByAndAggregateTransformationConfig;

      // Clone to avoid mutating the original constant
      const defaultTransformationFields: FieldsResponse = {
        requestFields: [...ADD_CUSTOM_LABEL_FIELD_RESPONSE.requestFields],
        responseFields: [...ADD_CUSTOM_LABEL_FIELD_RESPONSE.responseFields]
      };

      let order = defaultTransformationFields.responseFields.length;

      // Add groupByField only if it doesn't exist
      const exists = defaultTransformationFields.responseFields.some(
        (f) => f.code === inputConfiguration.groupByField
      );

      if (!exists) {
        defaultTransformationFields.responseFields.push({
          code: inputConfiguration.groupByField,
          codeChain: inputConfiguration.groupByField,
          description: `Value of ${inputConfiguration.groupByField}`,
          displayName: inputConfiguration.groupByField,
          order: order++,
          type: "STRING",
          fields: []
        });
      }

      // Add aggregate arrays
      for (const agg of inputConfiguration.aggregateArrays) {
        const arrayFields: AppField[] = agg.fields.map((f, idx) => ({
          code: f,
          codeChain: f,
          description: `Value of ${f}`,
          displayName: f,
          order: idx,
          type: "STRING",
          fields: []
        }));

        defaultTransformationFields.responseFields.push({
          code: agg.name,
          codeChain: agg.name,
          description: `Value of ${agg.name}`,
          displayName: agg.name,
          order: order++,
          type: "object", // TODO Consult with team if this should be ARRAY or OBJECT
          fields: arrayFields
        });
      }

      return {
        requestFields: [...defaultTransformationFields.requestFields],
        responseFields: mergeFirstLayerStringFields(
          defaultTransformationFields.responseFields,
          additionalPreviousFields
        )
      };
    }

    /**
     * MAP_OBJECTS
     */
    case TransformationStepType.MAP_OBJECTS: {
      const inputConfig = config as { arrayConfigs?: MapObjectConfigPayload[] };
      const responseFields = cloneAppFields(additionalPreviousFields?.responseFields || []);

      (inputConfig.arrayConfigs || []).forEach((arr) => {
        const existingIndex = responseFields.findIndex((field) => field.code === arr.mapObjectName);
        const baseObjectField = existingIndex !== -1 ? responseFields[existingIndex] : undefined;
        const childFieldMap = new Map<string, AppField>();

        arr.fieldMapArray?.forEach((mapGroup) => {
          mapGroup.mappings.forEach((m) => {
            if (childFieldMap.has(m.to)) return;

            childFieldMap.set(m.to, {
              code: m.to,
              codeChain: m.to,
              description: `Mapped from '${m.from}'`,
              displayName: m.to,
              order: childFieldMap.size,
              type: "STRING",
              fields: []
            });
          });
        });

        const mergedFields = Array.from(childFieldMap.values()).map((field, idx) => ({
          ...field,
          order: idx
        }));

        const mappedObject: AppField = {
          code: arr.mapObjectName,
          codeChain: arr.mapObjectName,
          description: `Mapped fields for ${arr.mapObjectName}`,
          displayName: arr.mapObjectName,
          order: baseObjectField?.order ?? responseFields.length,
          type: "object",
          fields: mergedFields
        };

        if (existingIndex !== -1) {
          responseFields[existingIndex] = mappedObject;
        } else {
          responseFields.push(mappedObject);
        }
      });

      const hasCustomLabel = responseFields.some((field) => field.code === CUSTOM_LABEL_CODE);
      if (!hasCustomLabel) {
        responseFields.push(
          ...ADD_CUSTOM_LABEL_FIELD_RESPONSE.responseFields.map((field, idx) => ({
            ...field,
            order: responseFields.length + idx
          }))
        );
      }

      return {
        requestFields: [...ADD_CUSTOM_LABEL_FIELD_RESPONSE.requestFields],
        responseFields: mergeFirstLayerStringFields(responseFields, additionalPreviousFields)
      };
    }

    /**
     * FANOUT
     */
    case TransformationStepType.FANOUT: {
      const inputConfig = config as FanoutTransformationConfig;

      let order = 0;

      if (!inputConfig.fanoutOn) {
        return {
          requestFields: [...defaultTransformationFields.requestFields],
          responseFields: mergeFirstLayerStringFields(
            defaultTransformationFields.responseFields,
            additionalPreviousFields
          )
        };
      }

      // Find the array field definition (e.g. "parents")
      const fanoutField = additionalPreviousFields?.responseFields.find(
        (f) => f.code === inputConfig.fanoutOn
      );

      // If array is not found, fallback
      if (!fanoutField || !fanoutField.fields) {
        return {
          requestFields: [...defaultTransformationFields.requestFields],
          responseFields: mergeFirstLayerStringFields(
            defaultTransformationFields.responseFields,
            additionalPreviousFields
          )
        };
      }

      // For FANOUT: each child field inside array becomes a STRING at root
      for (const inner of fanoutField.fields) {
        defaultTransformationFields.responseFields.push({
          code: inner.code,
          codeChain: inner.code,
          description: `Fanout field ${inner.code}`,
          displayName: inner.code,
          order: order++,
          type: "STRING", // Always string per your requirement
          fields: []
        });
      }

      return {
        requestFields: [...defaultTransformationFields.requestFields],
        responseFields: mergeFirstLayerStringFields(
          defaultTransformationFields.responseFields,
          additionalPreviousFields
        )
      };
    }

    /**
     * FLATTEN ARRAY FIELDS
     */
    case TransformationStepType.FLATTEN_ARRAY_FIELDS: {
      const inputConfig = config as { arrayConfigs?: FlattenObjectConfigPayload[] };
      const arrayConfigs = inputConfig.arrayConfigs || [];
      let order = defaultTransformationFields.responseFields.length;

      arrayConfigs.forEach((arr) => {
        arr.fieldFlattenArray?.forEach((mapGroup) => {
          mapGroup.mappings.forEach((m) => {
            defaultTransformationFields.responseFields.push({
              code: m.to,
              codeChain: m.to,
              description: `Flattened from '${arr.flattenArrayFieldName}.${m.from}'`,
              displayName: m.to,
              order: order++,
              type: "STRING",
              fields: []
            });
          });
        });
      });

      return {
        requestFields: [...defaultTransformationFields.requestFields],
        responseFields: mergeFirstLayerStringFields(
          defaultTransformationFields.responseFields,
          additionalPreviousFields
        )
      };
    }

    /**
     * ADD NEW FIELDS
     */
    case TransformationStepType.ADD_NEW_FIELDS: {

      const inputConfig = config as AddNewFieldsConfiguration;
      let order = defaultTransformationFields.responseFields.length;

      if (!inputConfig.fieldConfigs || inputConfig.fieldConfigs.length === 0) {
        return {
          requestFields: [...defaultTransformationFields.requestFields],
          responseFields: mergeFirstLayerStringFields(
            defaultTransformationFields.responseFields,
            additionalPreviousFields
          )
        };
      }

      for (const fc of inputConfig.fieldConfigs) {
        defaultTransformationFields.responseFields.push({
          code: fc.fieldName,
          codeChain: fc.fieldName,
          description: `Generated field ${fc.fieldName}`,
          displayName: fc.fieldName,
          order: order++,
          type: "STRING",
          fields: []
        });
      }

      return {
        requestFields: [...defaultTransformationFields.requestFields],
        responseFields: mergeFirstLayerStringFields(
          defaultTransformationFields.responseFields,
          additionalPreviousFields
        )
      };
    }

    default:
      return {
        requestFields: [...defaultTransformationFields.requestFields],
        responseFields: mergeFirstLayerStringFields(
          defaultTransformationFields.responseFields,
          additionalPreviousFields
        )
      };
  }
};
